import { Injectable } from '@angular/core'
import { nginV2Headers, SiteMembership } from '@se-po/shared-data-access-models'
import { map, Observable, throwError } from 'rxjs'
import { shareReplay } from 'rxjs/operators'
import { SeFeApiService } from 'se-fe-api'

@Injectable({
  providedIn: 'root',
})
export class SiteMembershipService {
  private mineCache$?: Observable<SiteMembership[]>

  constructor(
    private api: SeFeApiService
  ) { }

  public cancel(membership: SiteMembership, params={}): Observable<SiteMembership> {
    this.clearMineCache()

    return this.api.put<SiteMembership>(
      `site_memberships/${membership.id}`,
      { status: 'declined'},
      { params , headers: nginV2Headers }
    ).pipe(map(response => response.result))
  }

  public findAll(personaId: string | number, params={}): Observable<SiteMembership[]> {
    return this.api.get<SiteMembership[]>(
      `site_memberships?persona_id=${personaId}`,
      { params , headers: nginV2Headers }
    ).pipe(map(response => response.result))
  }

  public mine(params = {}): Observable<SiteMembership[]> {
    if (Object.keys(params).length > 0) {
      return this.fetchMine(params)
    }

    if (!this.mineCache$) {
      this.mineCache$ = this.fetchMine().pipe(
        shareReplay({ bufferSize: 1, refCount: false })
      )
    }

    return this.mineCache$
  }

  public clearMineCache(): void {
    this.mineCache$ = undefined
  }

  public archive(personaIds: string[], orgId: number): Observable<SiteMembership[]> {
    if (!personaIds.length) return throwError(() => new Error('No persona IDs provided'))

    this.clearMineCache()

    return this.api.put<SiteMembership[]>(
      `ngin/v2/site_memberships/archive`,
      { persona_ids: personaIds.map(id => parseInt(id, 10)), org_id: orgId },
      { headers: nginV2Headers }
    ).pipe(map(response => response.result))
  }

  public unarchive(personaIds: string[], orgId: number): Observable<SiteMembership[]> {
    if (!personaIds.length) return throwError(() => new Error('No persona IDs provided'))

    this.clearMineCache()

    return this.api.put<SiteMembership[]>(
      `ngin/v2/site_memberships/unarchive`,
      { persona_ids: personaIds.map(id => parseInt(id, 10)), org_id: orgId },
      { headers: nginV2Headers }
    ).pipe(map(response => response.result))
  }

  public block(personaIds: string[], orgId: number): Observable<SiteMembership[]> {
    if (!personaIds.length) return throwError(() => new Error('No persona IDs provided'))

    this.clearMineCache()

    return this.api.put<SiteMembership[]>(
      `ngin/v2/site_memberships/block`,
      { persona_ids: personaIds.map(id => parseInt(id, 10)), org_id: orgId },
      { headers: nginV2Headers }
    ).pipe(map(response => response.result))
  }

  public unblock(personaIds: string[], orgId: number): Observable<SiteMembership[]> {
    if (!personaIds.length) return throwError(() => new Error('No persona IDs provided'))

    this.clearMineCache()

    return this.api.put<SiteMembership[]>(
      `ngin/v2/site_memberships/unblock`,
      { persona_ids: personaIds.map(id => parseInt(id, 10)), org_id: orgId },
      { headers: nginV2Headers }
    ).pipe(map(response => response.result))
  }

  private fetchMine(params = {}): Observable<SiteMembership[]> {
    return this.api.get<SiteMembership[]>(
      'site_memberships/mine',
      { params, headers: nginV2Headers }
    ).pipe(map(response => response.result))
  }
}
